"use client";

import { logError } from "@/services/DatadogService";
import React, { useEffect, useRef } from "react";

interface AsyncThemeLinkProps {
  themeCssFilename: string;
}

export function AsyncThemeLink({ themeCssFilename }: AsyncThemeLinkProps) {
  const linkRef = useRef<HTMLLinkElement>(null);

  /**
   * Asynchronous CSS activation to improve FCP/LCP performance.
   * Initially loaded with media="print" to prevent render-blocking.
   * This effect switches it to media="all" once the stylesheet is
   * loaded, already available in cache, or fails (graceful fallback).
   */
  useEffect(() => {
    const link = linkRef.current;
    if (!link) return;

    const enableStyles = () => {
      link.media = "all";
      link.dataset.loaded = "true";
    };

    const handleError = (e: Event) => {
      logError(new Error(`Failed to load theme: ${themeCssFilename}`), {
        component: "AsyncThemeLink",
        details: e,
      });
      enableStyles();
    };

    try {
      if (link.sheet && link.sheet.cssRules.length > 0) {
        enableStyles();
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      if (link.sheet) {
        enableStyles();
        return;
      }
    }

    link.addEventListener("load", enableStyles);
    link.addEventListener("error", handleError);

    return () => {
      link.removeEventListener("load", enableStyles);
      link.removeEventListener("error", handleError);
    };
  }, [themeCssFilename]);

  return (
    <>
      <link
        ref={linkRef}
        rel="stylesheet"
        href={`/${themeCssFilename}`}
        media="print"
      />
      <noscript>
        <link
          rel="stylesheet"
          href={`/${themeCssFilename}`}
        />
      </noscript>
    </>
  );
}
